package com.macrofocus.eflux.network.substation

import com.macrofocus.eflux.deckgl.toJSonArray
import com.macrofocus.eflux.ui.morphing.adjustPolygonOrientation
import com.macrofocus.eflux.ui.morphing.align
import com.macrofocus.eflux.ui.morphing.isClockwise
import com.macrofocus.eflux.ui.morphing.mergePointsAtRelativeDistances
import org.locationtech.jts.geom.Geometry
import org.locationtech.jts.geom.MultiPolygon
import org.locationtech.jts.geom.Polygon

sealed interface GeometrySubstationData
: SubstationCapability {
    val name: String?
    val type: String?
    val logicalGeometry: Geometry?
    val physicalGeometry: Geometry?

    val logicalPolygon: Polygon?
    val physicalPolygon: Polygon?

    val combined: Pair<Polygon,Polygon?>

    val logicalPolygonArray: Array<Array<Double>>?
    val physicalPolygonArray: Array<Array<Double>>?

    companion object {
        val empty = EagerGeometrySubstationData(null, null, null, null)
    }
}

abstract class AbstractGeometrySubstationData : GeometrySubstationData {
    override val logicalPolygon: Polygon?
        get() = combined.first
    override val physicalPolygon: Polygon?
        get() = combined.second

    override val logicalPolygonArray: Array<Array<Double>>? by lazy {
        logicalPolygon?.toJSonArray()
    }

    override val physicalPolygonArray: Array<Array<Double>>? by lazy {
        physicalPolygon?.toJSonArray()
    }

    override val combined: Pair<Polygon,Polygon?> by lazy {
        var physicalGeometry = physicalGeometry

        if (physicalGeometry != null && logicalGeometry is Polygon && physicalGeometry is Polygon) {
            if ((logicalGeometry as Polygon).isClockwise() != physicalGeometry.isClockwise()) {
                println("$name is clockwise=${physicalGeometry.isClockwise()} when it should be ${(logicalGeometry as Polygon).isClockwise()} ")
                physicalGeometry =
                    adjustPolygonOrientation(physicalGeometry, !physicalGeometry.isClockwise())
            }
        }

        val (combinedLogical, combinedPhysical) = if (logicalGeometry is Polygon && physicalGeometry is Polygon) mergePointsAtRelativeDistances(
            logicalGeometry as Polygon,
            physicalGeometry
        ) else if (logicalGeometry is Polygon && physicalGeometry is MultiPolygon)
            mergePointsAtRelativeDistances(
                logicalGeometry as Polygon,
                physicalGeometry.getGeometryN(0) as Polygon
            ) else Pair(logicalGeometry, null)

        val physicalPolygon = combinedPhysical
        val logicalPolygon = if (combinedPhysical is Polygon && combinedLogical is Polygon) align(
            combinedPhysical,
            combinedLogical
        ) else if (combinedLogical is Polygon) combinedLogical else null
        Pair(logicalPolygon!!, physicalPolygon )
    }

    override fun toString(): String {
        return "AbstractGeometrySubstationData(name=$name, type=$type, logicalGeometry=$logicalGeometry, physicalGeometry=$physicalGeometry)"
    }
}

class LazyGeometrySubstationData (
    nameProvider: () -> String?,
    typeProvider: () -> String?,
    logicalGeometryProvider: () -> Geometry?,
    physicalGeometryProvider: () -> Geometry?
) : AbstractGeometrySubstationData(), GeometrySubstationData {
    override val name: String? by lazy { nameProvider() }
    override val type: String? by lazy { typeProvider() }
    override val logicalGeometry: Geometry? by lazy { logicalGeometryProvider() }
    override val physicalGeometry: Geometry? by lazy { physicalGeometryProvider() }

    override fun toString(): String {
        return "LazyGeometrySubstationData(name=$name, type=$type, logicalGeometry=$logicalGeometry, physicalGeometry=$physicalGeometry)"
    }
}

class EagerGeometrySubstationData (
    override val name: String?,
    override val type: String?,
    override val logicalGeometry: Geometry?,
    override val physicalGeometry: Geometry?
) : AbstractGeometrySubstationData(), GeometrySubstationData {
    override fun toString(): String {
        return "EagerGeometrySubstationData(name=$name, type=$type, logicalGeometry=$logicalGeometry, physicalGeometry=$physicalGeometry)"
    }
}